import {
  breadcrumbsIntegration,
  BrowserOptions,
  captureConsoleIntegration,
  dedupeIntegration,
  extraErrorDataIntegration,
  httpClientIntegration,
  reactRouterV7BrowserTracingIntegration,
} from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { beforeSend } from '../functions/beforeSend';
import { getRelease } from '../functions/getRelease';
import { getTracesSampleRate } from '../functions/getTracesSampleRate';

export const sentryOptions: BrowserOptions = {
  beforeSend,
  debug: false,
  // Reject events whose entire stack trace lives in a browser extension or
  // other non-app surface. These are virtually always third-party noise.
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
  ],
  dsn: 'https://20adc36c08104533969c4807d9169de5@o92107.ingest.sentry.io/1398689',
  environment: import.meta.env.VITE_APP_ENV,
  // Common low-signal noise: ResizeObserver loop messages are benign browser
  // chatter; "Non-Error promise rejection" with empty bodies has no actionable
  // detail; cancelled fetches surface as "Failed to fetch" during navigation.
  ignoreErrors: [
    't.blacklistC',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Load failed',
  ],
  // Tags applied to every event so they're filterable in the dashboard
  // without requiring a runtime hook to set them.
  initialScope: {
    tags: {
      appVersion: import.meta.env.VITE_APP_VERSION ?? 'local',
      commitSha: import.meta.env.VITE_APP_COMMIT_HASH ?? 'local',
    },
  },
  integrations: [
    reactRouterV7BrowserTracingIntegration({
      createRoutesFromChildren,
      matchRoutes,
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
    }),
    // Capture detailed breadcrumbs for user interaction context leading up
    // to the error: navigation, console output, network requests, DOM clicks,
    // and form input.
    breadcrumbsIntegration({
      console: true,
      dom: { serializeAttribute: ['data-testid', 'data-analytics-name'] },
      fetch: true,
      history: true,
      sentry: true,
      xhr: true,
    }),
    captureConsoleIntegration({ levels: ['error'] }),
    dedupeIntegration(),
    extraErrorDataIntegration({ depth: 6 }),
    httpClientIntegration(),
  ],
  // Default is 100; keep it explicit to document the budget.
  maxBreadcrumbs: 100,
  // Increase from the default (3) so nested error data (ex. HTTP client config,
  // request bodies, response payloads) survives serialization.
  normalizeDepth: 6,
  release: getRelease(),
  sendDefaultPii: true,
  tracesSampleRate: getTracesSampleRate(),
};
